import React from 'react';
import { AnimatePresence, motion } from 'motion/react';

/** Cap on the animated progress fill until the request actually finishes — keeps the bar from ever reading 100% prematurely. */
const PROGRESS_CAP_UNTIL_COMPLETE = 0.88;

/** Assumed upload rate used to animate the bar at a believable pace. */
const ANIMATED_UPLOAD_BYTES_PER_SECOND = 512 * 1024;

/** ms between progress ticks. */
const PROGRESS_TICK_MS = 100;

/** Circumference of the SVG ring (2πr, r=26). */
const RING_CIRCUMFERENCE = 163.363;

type ProgressRow = { name: string; size: number; progress: number };

/**
 * Per-file animated progress ring shown while a multipart upload is in
 * flight. Each row animates toward `PROGRESS_CAP_UNTIL_COMPLETE`, then snaps
 * to 100% and flips to a green check mark the moment the parent reports
 * `complete`.
 *
 * The component is intentionally fake (no actual byte counter is available
 * from the browser's fetch API) — it just gives the user real-time feedback
 * that something is happening while the route action runs.
 */
export function LibraryFileUploadProgress(props: { files: File[]; complete: boolean }) {
  const [rows, setRows] = React.useState<ProgressRow[]>(() =>
    props.files.map((file) => ({ name: file.name, size: file.size, progress: 0 }))
  );

  React.useEffect(() => {
    if (props.complete) return;

    const interval = setInterval(() => {
      setRows((prev) => {
        const incompleteIndex = prev.findIndex((r) => r.progress < PROGRESS_CAP_UNTIL_COMPLETE);
        if (incompleteIndex === -1) return prev;
        return prev.map((row, index) => {
          if (index !== incompleteIndex) return row;
          const jitter = 0.8 + Math.random() / 2.5;
          const delta = (jitter * ANIMATED_UPLOAD_BYTES_PER_SECOND) / 10 / row.size;
          return {
            ...row,
            progress: Math.min(row.progress + delta, PROGRESS_CAP_UNTIL_COMPLETE)
          };
        });
      });
    }, PROGRESS_TICK_MS);

    return () => clearInterval(interval);
  }, [props.complete]);

  React.useEffect(() => {
    if (props.complete) {
      setRows((prev) => prev.map((row) => ({ ...row, progress: 1 })));
    }
  }, [props.complete]);

  return (
    <div className="flex flex-col gap-y-2">
      {rows.map((row) => {
        const displayProgress = props.complete
          ? 1
          : Math.min(row.progress, PROGRESS_CAP_UNTIL_COMPLETE);
        return (
          <div key={row.name} className="flex flex-row items-center justify-between gap-x-4">
            <span className="flex-1 truncate text-sm text-neutral-700 dark:text-neutral-300">
              {row.name}
            </span>
            <div className="relative size-8">
              <div className="absolute inset-0">
                <AnimatePresence>
                  {props.complete ? (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="size-full rounded-full bg-emerald-500 flex items-center justify-center"
                    >
                      <svg className="size-3 text-white" viewBox="0 0 16 16" fill="currentColor">
                        <path
                          fillRule="evenodd"
                          d="M13.366 4.234a.8.8 0 010 1.132l-6.4 6.4a.8.8 0 01-1.132 0l-3.2-3.2a.8.8 0 011.132-1.132L6.4 10.07l5.834-5.835a.8.8 0 011.132 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </motion.div>
                  ) : (
                    <motion.svg
                      className="size-full"
                      viewBox="0 0 64 64"
                      initial={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      <motion.circle
                        className="text-neutral-100"
                        strokeWidth="8"
                        stroke="currentColor"
                        fill="transparent"
                        r="26"
                        cx="32"
                        cy="32"
                      />
                      <motion.circle
                        className="text-sky-500 dark:text-sky-400"
                        strokeWidth="8"
                        initial={{ strokeDasharray: `0 ${RING_CIRCUMFERENCE}` }}
                        animate={{
                          strokeDasharray: `${displayProgress * RING_CIRCUMFERENCE} ${RING_CIRCUMFERENCE}`
                        }}
                        transition={{ duration: 0.5 }}
                        strokeLinecap="round"
                        stroke="currentColor"
                        fill="transparent"
                        r="26"
                        cx="32"
                        cy="32"
                      />
                    </motion.svg>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
